import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { modulesAPI, degreesAPI, filtersAPI, attendanceAPI } from '../services/api';
import axios from 'axios';
import config from '../config/environment';

const ReportsAnalytics = () => {
  const [activeTab, setActiveTab] = useState('student-attendance');
  const [modules, setModules] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  
  // Filtered options for cascading dropdowns
  const [filteredDegrees, setFilteredDegrees] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    degreeLevel: '',
    academicStatus: '',
    academicYear: '',
    degree: '',
    year: '',
    semester: '',
    group: '',
    module: '',
    studentWithId: '',
    dateFrom: '',
    dateTo: ''
  });

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Cascading filter effects
  
  // Effect for Degree Level and Academic Status changes - filters degrees
  useEffect(() => {
    const fetchFilteredDegrees = async () => {
      try {
        if (filters.degreeLevel || filters.academicStatus) {
          const params = {};
          if (filters.degreeLevel) params.degreeLevel = filters.degreeLevel;
          if (filters.academicStatus) params.academicStatus = filters.academicStatus;
          
          console.log('🎓 Filtering degrees with params:', params);
          const response = await degreesAPI.getFilteredDegrees(params);
          console.log('🎓 Filtered degrees response:', response.data);
          setFilteredDegrees(response.data || []);
        } else {
          setFilteredDegrees(degrees); // Show all degrees when no filters selected
        }
      } catch (error) {
        console.error('Error fetching filtered degrees:', error);
        setFilteredDegrees([]);
      }
    };

    fetchFilteredDegrees();
  }, [filters.degreeLevel, filters.academicStatus, degrees]);

  // Effect for Degree changes - filters groups
  useEffect(() => {
    const fetchFilteredGroups = async () => {
      try {
        if (filters.degree) {
          const response = await filtersAPI.getFilteredGroups({
            degreeLevel: filters.degreeLevel,
            academicStatus: filters.academicStatus,
            degree: filters.degree,
            year: filters.year,
            semester: filters.semester,
            academicYear: filters.academicYear
          });
          setFilteredGroups(response.data || []);
        } else {
          setFilteredGroups([]); // Clear groups when no degree selected
        }
      } catch (error) {
        console.error('Error fetching filtered groups:', error);
        setFilteredGroups([]);
      }
    };

    fetchFilteredGroups();
  }, [filters.degreeLevel, filters.academicStatus, filters.degree, filters.year, filters.semester, filters.academicYear]);

  // Effect for Group changes - filters modules
  useEffect(() => {
    const fetchFilteredModules = async () => {
      try {
        if (filters.group) {
          const response = await filtersAPI.getFilteredModules({
            degree: filters.degree,
            year: filters.year,
            semester: filters.semester,
            group: filters.group,
            academicYear: filters.academicYear
          });
          setFilteredModules(response.data || []);
        } else {
          setFilteredModules(modules); // Show all modules when no group selected
        }
      } catch (error) {
        console.error('Error fetching filtered modules:', error);
        setFilteredModules([]);
      }
    };

    fetchFilteredModules();
  }, [filters.degree, filters.year, filters.semester, filters.group, filters.academicYear, modules]);

  // Effect for Module changes - filters students
  useEffect(() => {
    const fetchFilteredStudents = async () => {
      try {
        if (filters.module || filters.group) {
          const response = await filtersAPI.getFilteredStudents({
            degreeLevel: filters.degreeLevel,
            academicStatus: filters.academicStatus,
            degree: filters.degree,
            year: filters.year,
            semester: filters.semester,
            group: filters.group,
            module: filters.module,
            academicYear: filters.academicYear
          });
          setFilteredStudents(response.data || []);
        } else {
          setFilteredStudents([]); // Clear students when no filters applied
        }
      } catch (error) {
        console.error('Error fetching filtered students:', error);
        setFilteredStudents([]);
      }
    };

    fetchFilteredStudents();
  }, [filters.degreeLevel, filters.academicStatus, filters.degree, filters.year, filters.semester, filters.group, filters.module, filters.academicYear]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [modulesRes, degreesRes] = await Promise.all([
        modulesAPI.getAllModules(),
        degreesAPI.getAllDegrees()
      ]);

      setModules(modulesRes.data || []);
      setDegrees(degreesRes.data || []);
      
      // Initialize filtered degrees with all degrees
      setFilteredDegrees(degreesRes.data || []);
      setFilteredModules(modulesRes.data || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceReport = async () => {
    try {
      setLoading(true);
      
      // If we have specific student and module selected, get individual attendance count
      if (filters.studentWithId && filters.module) {
        console.log('🎯 Fetching individual student attendance count...');
        
        const attendanceCountResponse = await attendanceAPI.getStudentAttendanceCount({
          student_id: filters.studentWithId,
          module_code: filters.module,
          group_name: filters.group || undefined, // Include group if selected
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined
        });

        console.log('📊 Student attendance count result:', attendanceCountResponse.data);
        
        // Format the data for display in the table
        const formattedData = [{
          student_name: attendanceCountResponse.data.student.name,
          student_id: attendanceCountResponse.data.student.id,
          module_name: attendanceCountResponse.data.module.name,
          module_code: attendanceCountResponse.data.module.code,
          group_name: attendanceCountResponse.data.group.name,
          attended_sessions: attendanceCountResponse.data.attendance_count,
          total_sessions: attendanceCountResponse.data.total_sessions,
          attendance_percentage: attendanceCountResponse.data.attendance_percentage,
          date_range: `${attendanceCountResponse.data.date_range.from} to ${attendanceCountResponse.data.date_range.to}`,
          attendance_records: attendanceCountResponse.data.attendance_records,
          session_details: attendanceCountResponse.data.session_details
        }];

        setAttendanceData(formattedData);
      } else if (filters.module && !filters.studentWithId) {
        // If module is selected but no specific student, get all students' attendance for that module
        console.log('📚 Fetching all students attendance for module:', filters.module);
        console.log('📚 Applied filters:', filters);
        
        const token = localStorage.getItem('token');
        
        // Find the selected module to get its ID
        const selectedModuleObj = filteredModules.find(m => m.module_code === filters.module);
        if (!selectedModuleObj) {
          console.error('Selected module not found in filteredModules:', filteredModules);
          throw new Error('Selected module not found');
        }
        
        console.log('📚 Found module:', selectedModuleObj);
        
        // Find the selected group to get its ID if group is selected
        let groupId = undefined;
        if (filters.group) {
          const selectedGroupObj = filteredGroups.find(g => g.group_name === filters.group);
          if (selectedGroupObj) {
            groupId = selectedGroupObj.id;
            console.log('📚 Found group:', selectedGroupObj);
          }
        }
        
        const params = {
          module_id: selectedModuleObj.id,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined
        };
        
        // Add group_id if we have a specific group selected
        if (groupId) {
          params.group_id = groupId;
        }
        
        console.log('📚 API request params:', params);
        
        const response = await axios.get(config.getApiUrl('/attendance/reports/student-module-attendance'), {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        // The backend returns all students for the module (filtered by group if specified)
        const moduleAttendanceData = response.data || [];
        console.log('📊 Module attendance data received:', moduleAttendanceData);
        
        // If we have group filter but no results, try to get students from that group for this module
        if (moduleAttendanceData.length === 0 && filters.group) {
          console.log('📚 No attendance data found. Checking if students exist in group for this module...');
          
          // Let's try without group filter to see if there are any students for this module
          const allModuleResponse = await axios.get(config.getApiUrl('/attendance/reports/student-module-attendance'), {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              module_id: selectedModuleObj.id,
              date_from: filters.dateFrom || undefined,
              date_to: filters.dateTo || undefined
            }
          });
          
          console.log('� All module data (no group filter):', allModuleResponse.data);
        }
        
        // Merge backend data with all students in group/module (show 0% attendance too)
        let formattedData = moduleAttendanceData.map(record => ({
          student_name: record.student_name,
          student_id: record.student_id,
          module_name: record.module_name,
          module_code: record.module_code,
          group_name: record.group_name || 'N/A',
          attended_sessions: record.attended_sessions,
          total_sessions: record.total_sessions,
          attendance_percentage: Math.round(record.attendance_percentage * 100) / 100, // Round to 2 decimals
          present_count: record.present_count || 0,
          absent_count: record.absent_count || 0,
          late_count: record.late_count || 0,
          excused_count: record.excused_count || 0
        }));

        // Only do this if group and module are selected
        if (filters.group && filters.module) {
          // Find students in filteredStudents not in formattedData
          const existingIds = new Set(formattedData.map(r => r.student_id));
          const totalSessions = formattedData.length > 0 ? formattedData[0].total_sessions : 0;
          const missingStudents = filteredStudents.filter(s => !existingIds.has(s.student_id));
          const groupName = filters.group;
          const moduleName = formattedData.length > 0 ? formattedData[0].module_name : (selectedModuleObj ? selectedModuleObj.module_name : '');
          const moduleCode = formattedData.length > 0 ? formattedData[0].module_code : (selectedModuleObj ? selectedModuleObj.module_code : '');
          for (const s of missingStudents) {
            formattedData.push({
              student_name: s.name,
              student_id: s.student_id,
              module_name: moduleName,
              module_code: moduleCode,
              group_name: groupName,
              attended_sessions: 0,
              total_sessions: totalSessions,
              attendance_percentage: 0,
              present_count: 0,
              absent_count: 0,
              late_count: 0,
              excused_count: 0
            });
          }
        }
        setAttendanceData(formattedData);
      } else {
        // Original general report logic - all attendance data
        console.log('📊 Fetching general attendance report...');
        const token = localStorage.getItem('token');
        
        const response = await axios.get(config.getApiUrl('/attendance/reports/student-module-attendance'), {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            date_from: filters.dateFrom || undefined,
            date_to: filters.dateTo || undefined
          }
        });

        setAttendanceData(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching attendance report:', error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Reset dependent filters when parent filters change
      if (field === 'degreeLevel' || field === 'academicStatus') {
        newFilters.degree = '';
        newFilters.year = '';
        newFilters.semester = '';
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'academicYear') {
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'degree') {
        newFilters.year = '';
        newFilters.semester = '';
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'year') {
        newFilters.semester = '';
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'semester') {
        newFilters.group = '';
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'group') {
        newFilters.module = '';
        newFilters.studentWithId = '';
      } else if (field === 'module') {
        newFilters.studentWithId = '';
      }
      
      return newFilters;
    });
  };

  const resetFilters = () => {
    setFilters({
      degreeLevel: '',
      academicStatus: '',
      academicYear: '',
      degree: '',
      year: '',
      semester: '',
      group: '',
      module: '',
      studentWithId: '',
      dateFrom: '',
      dateTo: ''
    });
    
    // Reset filtered arrays
    setFilteredDegrees(degrees);
    setFilteredGroups([]);
    setFilteredModules(modules);
    setFilteredStudents([]);
    
    setAttendanceData([]);
  };

  const downloadCSV = () => {
    if (attendanceData.length === 0) {
      alert('No attendance data to download. Please generate a report first.');
      return;
    }

    // Generate filename with current date and filters
    const currentDate = new Date().toISOString().split('T')[0];
    let filename = `attendance-report-${currentDate}`;
    
    // Add filter context to filename
    if (filters.group && filters.module) {
      filename += `-${filters.group}-${filters.module}`;
    } else if (filters.module) {
      filename += `-${filters.module}`;
    } else if (filters.group) {
      filename += `-${filters.group}`;
    }
    filename += '.csv';

    // CSV Headers
    const headers = [
      'Student Name',
      'Student ID', 
      'Module Name',
      'Module Code',
      'Group Name',
      'Classes Attended',
      'Total Sessions',
      'Attendance Percentage'
    ];

    // Convert data to CSV format
    const csvContent = [
      // Add title row
      [`"Attendance Report - Generated on ${new Date().toLocaleDateString()}"`],
      // Add filter information
      [`"Filters: ${Object.entries(filters).filter(([key, value]) => value).map(([key, value]) => `${key}: ${value}`).join(', ')}"`],
      // Add empty row
      [''],
      // Add headers
      headers,
      // Add data rows
      ...attendanceData.map(record => [
        record.student_name || '',
        record.student_id || '',
        record.module_name || '',
        record.module_code || '',
        record.group_name || '',
        record.attended_sessions || 0,
        record.total_sessions || 0,
        `${record.attendance_percentage || 0}%`
      ])
    ].map(row => row.join(',')).join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`📥 Downloaded attendance report: ${filename}`);
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 80) return '#10B981'; // Green
    if (percentage >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const FilterSection = () => (
    <div style={{
      background: 'white',
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '600',
        marginBottom: '20px',
        color: '#374151',
        fontFamily: 'Outfit'
      }}>
        📊 Filter Options
      </h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '20px'
      }}>
        {/* Degree Level */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            🎓 Degree Level
          </label>
          <select
            value={filters.degreeLevel}
            onChange={(e) => handleFilterChange('degreeLevel', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Degree Level</option>
            <option value="undergraduate">Undergraduate</option>
            <option value="postgraduate">Postgraduate</option>
            <option value="doctoral">Doctoral</option>
          </select>
        </div>

        {/* Academic Status */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            � Academic Status
          </label>
          <select
            value={filters.academicStatus}
            onChange={(e) => handleFilterChange('academicStatus', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Academic Status</option>
            <option value="local">Local</option>
            <option value="international">International</option>
          </select>
        </div>

        {/* Academic Year */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📚 Academic Year
          </label>
          <select
            value={filters.academicYear}
            onChange={(e) => handleFilterChange('academicYear', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Academic Year</option>
            <option value="2023-2024">2023-2024</option>
            <option value="2024-2025">2024-2025</option>
            <option value="2025-2026">2025-2026</option>
            <option value="2026-2027">2026-2027</option>
            <option value="2027-2028">2027-2028</option>
          </select>
        </div>

        {/* Degree */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            🎯 Degree
          </label>
          <select
            value={filters.degree}
            onChange={(e) => handleFilterChange('degree', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Degree</option>
            {filteredDegrees.map(degree => (
              <option key={degree.id} value={degree.degree_programme}>
                {degree.degree_programme} ({degree.degree_level})
              </option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📅 Year
          </label>
          <select
            value={filters.year}
            onChange={(e) => handleFilterChange('year', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Year</option>
            <option value="1">Year 1</option>
            <option value="2">Year 2</option>
            <option value="3">Year 3</option>
            <option value="4">Year 4</option>
          </select>
        </div>

        {/* Semester */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📆 Semester
          </label>
          <select
            value={filters.semester}
            onChange={(e) => handleFilterChange('semester', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Semester</option>
            <option value="1">Semester 1</option>
            <option value="2">Semester 2</option>
          </select>
        </div>

        {/* Group */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            👥 Group
          </label>
          <select
            value={filters.group}
            onChange={(e) => handleFilterChange('group', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Group</option>
            {filteredGroups.map(group => (
              <option key={group.id} value={group.group_name}>
                {group.group_name}
              </option>
            ))}
          </select>
        </div>

        {/* Module */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📖 Module
          </label>
          <select
            value={filters.module}
            onChange={(e) => handleFilterChange('module', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Module</option>
            {filteredModules.map(module => (
              <option key={module.id} value={module.module_code}>
                {module.module_name} ({module.module_code})
              </option>
            ))}
          </select>
        </div>

        {/* Student with ID */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            👨‍🎓 Student with ID
          </label>
          <select
            value={filters.studentWithId}
            onChange={(e) => handleFilterChange('studentWithId', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Student</option>
            {filteredStudents.map(student => (
              <option key={student.student_id} value={student.student_id}>
                {student.name} ({student.student_id})
              </option>
            ))}
          </select>
        </div>

        {/* Date From */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📅 From Date
          </label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white'
            }}
          />
        </div>

        {/* Date To */}
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            color: '#374151',
            fontFamily: 'Outfit'
          }}>
            📅 To Date
          </label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Outfit',
              background: 'white'
            }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <button
          onClick={fetchAttendanceReport}
          disabled={loading}
          style={{
            padding: '12px 24px',
            background: '#F04915',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Outfit',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.2s'
          }}
        >
          {loading ? '🔄 Loading...' : '📊 Generate Report'}
        </button>

        <button
          onClick={resetFilters}
          style={{
            padding: '12px 24px',
            background: '#6B7280',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Outfit',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          🔄 Reset Filters
        </button>

        <button
          onClick={downloadCSV}
          disabled={attendanceData.length === 0}
          style={{
            padding: '12px 24px',
            background: attendanceData.length === 0 ? '#D1D5DB' : '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Outfit',
            cursor: attendanceData.length === 0 ? 'not-allowed' : 'pointer',
            opacity: attendanceData.length === 0 ? 0.5 : 1,
            transition: 'all 0.2s'
          }}
        >
          📥 Download Report as CSV
        </button>
      </div>
    </div>
  );

  const AttendanceTable = () => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    }}>
      <div style={{
        background: '#F04915',
        padding: '16px 24px',
        color: 'white'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          margin: 0,
          fontFamily: 'Outfit'
        }}>
          {filters.studentWithId && filters.module 
            ? `👨‍🎓 Individual Student Attendance Report - ${filteredStudents.find(s => s.student_id === filters.studentWithId)?.name || 'Selected Student'}`
            : filters.module && !filters.studentWithId
            ? `📚 Module Attendance Report - ${filteredModules.find(m => m.module_code === filters.module)?.module_name || 'Selected Module'} (All Students)`
            : '📈 General Student Attendance Report'
          }
        </h3>
        {(filters.module && !filters.studentWithId) && (
          <p style={{
            fontSize: '14px',
            margin: '8px 0 0 0',
            opacity: 0.9,
            fontFamily: 'Outfit'
          }}>
            Showing attendance percentages for all students enrolled in this module
          </p>
        )}
      </div>

      {attendanceData.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#6B7280',
          fontSize: '16px',
          fontFamily: 'Outfit'
        }}>
          {loading ? (
            <div>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>🔄</div>
              <div>Loading attendance data...</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>📊</div>
              <div>No attendance data found. Please adjust your filters and try again.</div>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Summary Statistics for Module Reports */}
          {filters.module && !filters.studentWithId && attendanceData.length > 1 && (
            <div style={{
              padding: '20px 24px',
              background: '#F8F9FA',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 12px 0',
                color: '#374151',
                fontFamily: 'Outfit'
              }}>
                📊 Module Summary
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '16px',
                fontSize: '14px',
                fontFamily: 'Outfit'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>Total Students</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
                    {attendanceData.length}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>Average Attendance</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
                    {(attendanceData.reduce((sum, record) => sum + (record.attendance_percentage || 0), 0) / attendanceData.length).toFixed(1)}%
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>Excellent (≥80%)</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#10B981' }}>
                    {attendanceData.filter(record => (record.attendance_percentage || 0) >= 80).length}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>Poor (&lt;60%)</div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#EF4444' }}>
                    {attendanceData.filter(record => (record.attendance_percentage || 0) < 60).length}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            fontFamily: 'Outfit'
          }}>
            <thead style={{ background: '#F9FAFB' }}>
              <tr>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Student
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Module
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Group
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Classes Attended
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Total Sessions
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Attendance %
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #E5E7EB', fontWeight: '600' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {attendanceData.map((record, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{record.student_name}</div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>ID: {record.student_id}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{record.module_name}</div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>{record.module_code}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      {record.group_name || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      background: '#10B981',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'inline-block',
                      minWidth: '40px'
                    }}>
                      {record.attended_sessions || record.attendance_count || 0}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      background: '#3B82F6',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'inline-block',
                      minWidth: '40px'
                    }}>
                      {record.total_sessions || 0}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      color: getAttendanceColor(record.attendance_percentage || 0),
                      fontWeight: '700',
                      fontSize: '16px'
                    }}>
                      {record.attendance_percentage !== undefined ? record.attendance_percentage.toFixed(1) : '0.0'}%
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: (record.attendance_percentage || 0) >= 80 ? '#D1FAE5' :
                                (record.attendance_percentage || 0) >= 60 ? '#FEF3C7' : '#FEE2E2',
                      color: (record.attendance_percentage || 0) >= 80 ? '#065F46' :
                             (record.attendance_percentage || 0) >= 60 ? '#92400E' : '#991B1B'
                    }}>
                      {(record.attendance_percentage || 0) >= 80 ? '✅ Excellent' :
                       (record.attendance_percentage || 0) >= 60 ? '⚠️ Average' : '❌ Poor'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: '#F9FAFB'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {/* Page Header */}
        <div style={{
          padding: '24px 40px',
          background: 'white',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontFamily: 'Outfit',
            fontWeight: '700',
            margin: 0,
            color: '#111827'
          }}>
            📊 Reports & Analytics
          </h1>
          <p style={{
            fontSize: '16px',
            fontFamily: 'Outfit',
            color: '#6B7280',
            margin: '8px 0 0 0'
          }}>
            Generate detailed attendance reports and analyze student performance
          </p>
        </div>

        {/* Main Content */}
        <div style={{ padding: '24px 40px' }}>
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            marginBottom: '24px',
            background: 'white',
            borderRadius: '12px',
            padding: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <button
              onClick={() => setActiveTab('student-attendance')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'student-attendance' ? '#F04915' : 'transparent',
                color: activeTab === 'student-attendance' ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Outfit',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              📈 Student Attendance Analysis
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'student-attendance' && (
            <>
              <FilterSection />
              <AttendanceTable />
            </>
          )}
        </div>

        {/* Back Button */}
        <div style={{
          padding: '24px 40px',
          borderTop: '1px solid #E5E7EB',
          background: 'white'
        }}>
          <Link to="/admin">
            <button 
              type="button" 
              style={{
                padding: '12px 24px',
                background: '#6B7280',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontFamily: 'Outfit',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#4B5563';
              }}
              onMouseOut={(e) => {
                e.target.style.background = '#6B7280';
              }}
            >
              ← Back to Dashboard
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReportsAnalytics;
